var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { translate } from '@utils/i18n';
import FilterPanel from '@components/FilterPanel';
import SearchPanel from '@components/SearchPanel';
import { Table, TableSize } from '@ui/molecules/Table/Table';
import { Toolbar } from '@ui/molecules/Toolbar';
import { Button, Checkbox, Popup, showSuccessToast, showErrorToast } from '@ui';
import CustomScrollbar from '@components/CustomScrollbar/CustomScrollbar';
import { COLUMNS, NAMESPACE, NON_HIDEABLE_COLUMN_KEYS, PAGE_SIZE } from './constants';
import { useFilters } from './hooks/useFilters';
import { useColumnVisibility } from './hooks/useColumnVisibility';
import { useProjectionData } from './hooks/useProjectionData';
import { triggerExportCsv, triggerExportSummary } from './services/projectionService';
import './SprinklerProjectionReportStyles.scss';
export function SprinklerProjectionTable({ buildingId }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const { filters, filtersList, 
    // setFilter, // re-enable with the collapse-groups control below
    setSearch, resetFilters, onResetCache, sortBy, sortDirection, setSortBy, hiddenColumns, setHiddenColumns } = useFilters(buildingId);
    const nonHideableKeys = useMemo(() => (buildingId ? [...NON_HIDEABLE_COLUMN_KEYS, 'system'] : NON_HIDEABLE_COLUMN_KEYS), [buildingId]);
    const { filterVisible, isHidden, toggle } = useColumnVisibility(hiddenColumns, setHiddenColumns, nonHideableKeys);
    const configureBtnRef = useRef(null);
    const [configureOpen, setConfigureOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selected, setSelected] = useState([]);
    const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const scopedFilters = useMemo(() => (buildingId === undefined ? filters : Object.assign(Object.assign({}, filters), { buildingIds: [buildingId] })), [filters, buildingId]);
    const { data, isFetching, isLoading } = useProjectionData(scopedFilters, currentPage, sortBy, sortDirection);
    const rows = ((_a = data === null || data === void 0 ? void 0 : data.rows) !== null && _a !== void 0 ? _a : []);
    const visibleFixedColumns = useMemo(() => {
        if (!buildingId)
            return COLUMNS;
        return COLUMNS.filter((c) => c.dataKey !== 'building' && c.dataKey !== 'territory').map((c) => c.dataKey === 'system' ? Object.assign(Object.assign({}, c), { columnType: 'primary' }) : c);
    }, [buildingId]);
    const fixedKeys = useMemo(() => new Set(COLUMNS.map((c) => c.dataKey)), []);
    const allColumns = useMemo(() => {
        var _a;
        const dynamic = ((_a = data === null || data === void 0 ? void 0 : data.columns) !== null && _a !== void 0 ? _a : [])
            .filter((col) => !fixedKeys.has(col.key))
            .map((col) => ({ dataKey: col.key, label: col.label }));
        return [...visibleFixedColumns.slice(0, -2), ...dynamic, ...visibleFixedColumns.slice(-2)];
    }, [data === null || data === void 0 ? void 0 : data.columns, fixedKeys, visibleFixedColumns]);
    const columns = useMemo(() => filterVisible(allColumns), [allColumns, filterVisible]);
    useEffect(() => {
        setCurrentPage(1);
    }, [scopedFilters, sortBy, sortDirection]);
    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        setSelected([]);
        setSelectAllAcrossPages(false);
    }, []);
    const handleSort = useCallback((dataKey, direction) => {
        setSortBy(direction ? dataKey : null, direction !== null && direction !== void 0 ? direction : 'asc');
        setCurrentPage(1);
    }, [setSortBy]);
    const handleSelectionChange = useCallback((id, checked) => {
        setSelectAllAcrossPages(false);
        setSelected((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
    }, []);
    const handleSelectAll = useCallback((all) => {
        setSelectAllAcrossPages(false);
        setSelected(all ? rows.map((r) => r.id) : []);
    }, [rows]);
    const handleSelectAllAcrossPages = useCallback(() => {
        setSelectAllAcrossPages(true);
        setSelected(rows.map((r) => r.id));
    }, [rows]);
    const handleExportCsv = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (isExporting)
            return;
        setIsExporting(true);
        try {
            const { message } = yield triggerExportCsv(scopedFilters, sortBy, sortDirection, selected, selectAllAcrossPages);
            showSuccessToast(message);
        }
        catch (_a) {
            showErrorToast(translate('export_error', { namespace: NAMESPACE, defaultValue: 'Export failed. Please try again.' }));
        }
        finally {
            setIsExporting(false);
        }
    }), [scopedFilters, sortBy, sortDirection, selected, selectAllAcrossPages, isExporting]);
    const handleExportSummary = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (isExporting)
            return;
        setIsExporting(true);
        try {
            const { message } = yield triggerExportSummary(scopedFilters, sortBy, sortDirection, selected, selectAllAcrossPages);
            showSuccessToast(message);
        }
        catch (_a) {
            showErrorToast(translate('export_error', { namespace: NAMESPACE, defaultValue: 'Export failed. Please try again.' }));
        }
        finally {
            setIsExporting(false);
        }
    }), [scopedFilters, sortBy, sortDirection, selected, selectAllAcrossPages, isExporting]);
    return (React.createElement("div", { className: "sprinkler-projection__content" },
        React.createElement(FilterPanel, { filters: filtersList, onResetFilters: resetFilters, onResetCache: onResetCache },
            React.createElement("div", { className: "filters__group--search" },
                React.createElement(SearchPanel, { placeholder: translate('filters.search_placeholder', { namespace: NAMESPACE }), onChange: setSearch }))),
        React.createElement("div", { className: "sprinkler-projection__toolbar-row" },
            React.createElement(Toolbar, null,
                selected.length > 0 && (React.createElement(Toolbar.Selection, { count: selectAllAcrossPages ? (_b = data === null || data === void 0 ? void 0 : data.total) !== null && _b !== void 0 ? _b : 0 : selected.length, totalCount: (_c = data === null || data === void 0 ? void 0 : data.total) !== null && _c !== void 0 ? _c : 0, selectAll: selectAllAcrossPages, onSelectAll: handleSelectAllAcrossPages, selectAllLabel: translate('select_all_label', {
                        namespace: NAMESPACE,
                        count: (_d = data === null || data === void 0 ? void 0 : data.total) !== null && _d !== void 0 ? _d : 0
                    }) })),
                React.createElement(Toolbar.Group, null,
                    React.createElement(Button, { onClick: handleExportSummary, disabled: selected.length === 0 || isExporting },
                        React.createElement("i", { className: "fa-light fa-file-lines" }),
                        " ",
                        translate('export_summary', { namespace: NAMESPACE })),
                    React.createElement(Button, { onClick: handleExportCsv, disabled: selected.length === 0 || isExporting },
                        React.createElement("i", { className: "fa-light fa-file-csv" }),
                        " ",
                        translate('export_csv', { namespace: NAMESPACE })))),
            React.createElement("div", { className: "sprinkler-projection__configure" },
                React.createElement(Button, { ref: configureBtnRef, isMenu: true, onClick: () => setConfigureOpen((open) => !open) },
                    React.createElement("i", { className: "fa-light fa-sliders" }),
                    " ",
                    translate('configure', { namespace: NAMESPACE })),
                React.createElement(Popup, { anchor: configureBtnRef.current, show: configureOpen, onClose: () => setConfigureOpen(false), position: "bottom-right", containerClass: "sprinkler-projection__configure-popup" },
                    React.createElement(Popup.Section, { fillHeight: true },
                        React.createElement(Popup.Title, null, translate('columns_shown', { namespace: NAMESPACE })),
                        allColumns
                            .filter((column) => !nonHideableKeys.includes(column.dataKey))
                            .map((column) => (React.createElement(Checkbox, { key: column.dataKey, id: `sprinkler-projection-col-${column.dataKey}`, checked: !isHidden(column.dataKey), onChange: () => toggle(column.dataKey), label: column.label }))))))),
        React.createElement("div", { className: "sprinkler-projection__table-area" },
            isLoading && (React.createElement("div", { className: "sprinkler-projection__loading" },
                React.createElement("i", { className: "fa-light fa-spinner-third fa-spin" }))),
            !isLoading && rows.length > 0 && (React.createElement(CustomScrollbar, null,
                React.createElement(Table, { columns: columns, data: rows, variant: "listing", size: TableSize.Full, selectable: true, sortable: true, onSort: handleSort, sortBy: sortBy, sortDirection: sortDirection, pageSize: PAGE_SIZE, onPageChange: handlePageChange, serverCurrentPage: (_e = data === null || data === void 0 ? void 0 : data.page) !== null && _e !== void 0 ? _e : currentPage, serverTotalPages: (_f = data === null || data === void 0 ? void 0 : data.total_pages) !== null && _f !== void 0 ? _f : 1, serverTotalItems: (_g = data === null || data === void 0 ? void 0 : data.total) !== null && _g !== void 0 ? _g : 0, getItemId: (item) => item.id, selectedItems: selected, onSelectionChange: handleSelectionChange, onSelectAll: handleSelectAll }))),
            !isLoading && !isFetching && rows.length === 0 && (React.createElement("div", { className: "sprinkler-projection__empty" },
                React.createElement("span", null, translate('no_results', { namespace: 'generic', defaultValue: 'No results found' })))))));
}
export default SprinklerProjectionTable;
